import * as React from "react";
import {cx} from "emotion";

import "./product-cards.scss";

import {FontAwesomeIcon}  from "@fortawesome/react-fontawesome";
import {faSpinner}        from "@fortawesome/free-solid-svg-icons";

import {isApiTrue}        from "@Common/logic/is-api-true";
import {isOiaItem}        from "@Common/logic/item/is-oia-item";
import {cs}               from "@Common/react/chain-services";
import {consumeContext}   from "@Common/react/context";
import {fragments}        from "@Common/react/fragments";
import {Invoke}           from "@Common/react/invoke";
import {keyed}            from "@Common/react/keyed";
import {Static2}          from "@Common/react/static-2";
import {UseState}         from "@Common/react/use-state";
import {ConfirmModal}     from "@Common/ui-components/confirm-modal/confirm-modal";
import {isDevQuan}        from "@Common/ui-components/debug/is-dev-quan";
import {AlertErrorDialog} from "@Common/ui-components/heavy/common/design2/dialogs/alert-error-dialog/alert-error-dialog";
import {formatMoney}      from "@Common/ui-components/language/format-money";
import {ErrorHighlighter} from "@Common/ui-components/light/error-highlighter";
import {scrollIntoView}   from "@Common/ui-components/light/scroll-into-view";
import {cascadeFind,
        cascadeSum}       from "@Common/utils/cascade";
import {sum}              from "@Common/utils/collections";
import {parseDateFormat}  from "@Common/utils/date/parse-date-format";
import {chain}            from "@Common/utils/fs";

import {CheckShowPricing} from "../../../../common/check-show-pricing";

import {Actions} from "./actions/actions";
import {Details} from "./details/details";
import {Qty}     from "./qty/qty";

export const ProductCardsSection = ({store, isFromApproval, next}) => cs(
    ["alertDialog", (_, next) => AlertErrorDialog({next})],
    ["error", (_, next) => next(
        (store.cart?.items.length > 0 && store.cart?.items.find(item => !item?.isAvailableItem) && {invalid: true, msg: ERROR_MESSAGE_UNAVAILABLE_ITEMS}) || 
        chain(
            cascadeFind(store.cart?.items, "[*].approvalInfo", (approval) => approval?.billingTotalExceeded && !approval?.enableApprovalButton && approval),
            (approval) => approval || cascadeFind(store.cart?.items, "[*].approvalInfo", (approval) => approval?.billingTotalExceeded && approval),
            (approval) => approval && {invalid: !approval.enableApprovalButton, msg: approval.error},
        )
    )],
    ({error, alertDialog}, next) => fragments(
        next(),
        error && Invoke({
            action: () => {
                alertDialog.show({message: error.msg});
            },
        })
    ),

    ["eh", (_, next) => ErrorHighlighter({next})],
    ["domRef", (_, next) => Static2({next})],
    ["confirmModal", (_, next) => ConfirmModal({next})],
    ({error, eh, domRef, confirmModal}) => next({
        ...error && {
            highlightErrors: () => {
                eh.start();
                scrollIntoView(domRef.get());
            },
            invalid: error.invalid,
        },
        render: () => (
            <div className={cx("product-cards-53d", eh.css)} ref={domRef.set}>
                <div className="header">
                    <div className="title">
                        Cart Review
                    </div>
                    <div className="sub-title">
                        Review items and option selections
                    </div>
                </div>
                {store.cart?.items.map((item) => cs(
                    keyed(item.itemId),
                    consumeContext('company'),
                    ["removing", (_, next) => UseState({next})],
                    ({company, removing}) => (
                        <div
                            className={cx("product", 
                                {
                                    "reproof": item?.isReproofRequired, removing: removing.value,
                                    "unavailable": !item?.isAvailableItem
                                }
                            )}
                            xd-qa-type="card"
                            xd-qa-name={item.itemId}
                        >
                            {removing.value && <>
                                <div className="unclickable-div">
                                    <div className="center-content">
                                        <div className="removing">Removing...</div>
                                        <FontAwesomeIcon icon={faSpinner} className="fa-pulse"/>
                                    </div>
                                </div>
                                <div className="overlay-div"></div>
                            </>}
                            <div className="image">
                                <img src={item.itemThumbnails?.[0]} alt={item.itemName}/>
                                {
                                    !item?.isAvailableItem &&
                                    <img className="out-of-stock-overlay"
                                        src={require("../../../../../../public/assets/out-of-stock-overlay.svg")}
                                    />
                                }
                            </div>

                            <div className="content">
                                <div className="line1">
                                    <div className="title-boundary">
                                        <div
                                            xd-qa-type="label"
                                            xd-qa-name="Item Name"
                                            className="title"
                                            title={item.itemName || item.metaData?.productName || "Brochure"}
                                        >
                                            {item.itemName || item.metaData?.productName || "Brochure"}
                                        </div>
                                    </div>

                                    {cs(
                                        ({}, next) => CheckShowPricing({next}),
                                        () => (
                                            <div
                                                xd-qa-type="label"
                                                xd-qa-name="Item Amount"
                                                className="amount"
                                            >
                                                {formatMoney(cascadeSum(item, "itemTotals[*]", (it) => it.type === "subTotal" ? it.amount : 0))}
                                            </div>
                                        )
                                    )}
                                </div>

                                {Actions({
                                    item,
                                    hasMultipleItems: store.cart.items.length > 1,
                                    store, isFromApproval, removing, confirmModal
                                })}

                                {isOiaItem(item) && item.metaData.templateName && (
                                    <div className="template-name">
                                        {item.metaData.templateName}
                                    </div>
                                )}

                                {(item.selections.distributionType === "shipping" || item.selections.distributionType === "pickup") ? (
                                    Qty({value: item.selections.quantity, xdQaName: "qty"})
                                ) : item.selections.distributionType === "mailing" && (
                                    Qty({value: ( sum(item.selections.mailingListIds, (ml) => ml.quantity) - item?.selections?.duplicateQuantity ) , xdQaName: "qty", sampleCard: (item?.displaySampleAddress)})
                                )}

                                {!isDevQuan() && (
                                    Details({item, company})
                                )}

                                {(
                                    item.selections.distributionType !== "download" &&
                                    isApiTrue(item.selections.futureFulfillment)
                                ) && (
                                    <div className="fulfillment-message" xd-qa-type="label" xd-qa-name="fulfillment message">
                                        {fulfillmentMessage(item)}
                                    </div>
                                )}
                                {(
                                    item.breakConsolidation === false &&
                                    item.isConsolidated === false &&
                                    store.cart?.items.filter(f => f.selections.distributionType === "shipping").length > 1
                                ) && (
                                    <div className="consolidation-message" xd-qa-type="label" xd-qa-name="consolidation message">
                                        This order is not available for shipping consolidation.
                                    </div>
                                )}
                            </div>
                        </div>
                    ),
                ))}
            </div>
        ),
    }),
);

const fulfillmentMessage = (item) => (
    "Fulfillment for this item is scheduled for " + parseDateFormat("mm/dd/yy").formatDate(
        parseDateFormat("yyyy-mm-dd").parseDate(item.selections.scheduledDate)
    )
);

const ERROR_MESSAGE_UNAVAILABLE_ITEMS = `This cart contains items that are currently unavailable. 
    Please remove or edit the highlighted item(s) to complete this order.`

